import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { finalize, Observable } from 'rxjs';
import { KorviuListComponent, KorviuLoaderComponent } from 'shared';
import { IKorviuListConfig, KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { IProduct } from 'core/types/product.interface';
import { CategoriesService } from 'core';
import { BreadcrumbComponent } from '../../../blocks/breadcrumb/breadcrumb.component';
import { createRewardProductFilterSections, createRewardProductSortSections } from '../reward-list-options';

@Component({
  selector: 'app-brand-products',
  imports: [KorviuListComponent, KorviuLoaderComponent, BreadcrumbComponent],
  templateUrl: './brand-products.component.html',
  styleUrls: ['./brand-products.component.scss']
})
export class BrandProductsComponent implements OnInit {
  readonly #categoriesService = inject(CategoriesService);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #destroyRef = inject(DestroyRef);

  readonly brandId = signal<number | null>(null);
  readonly brandName = signal<string | null>(null);
  readonly brandIsLoading = signal<boolean>(false);

  readonly productListConfig: IKorviuListConfig = {
    getFn: this.getProducts.bind(this),
    sortSections: createRewardProductSortSections(),
    filterSections: createRewardProductFilterSections(),
    doNotSortFilterSections: true,
    itemType: KorviuListItemTypes.PRODUCT_LIST,
    totalLocalizableName: 'rewards.rewards',
    pagination: { pageSize: 12, page: 1 }
  };

  ngOnInit(): void {
    const id = this.#activatedRoute.snapshot.paramMap.get('id');
    if (id) {
      this.brandId.set(+id);
      this.#loadBrandName(+id);
    }
  }

  #loadBrandName(brandId: number): void {
    this.brandIsLoading.set(true);
    this.#categoriesService
      .getBrandById(brandId)
      .pipe(
        finalize(() => this.brandIsLoading.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(brand => {
        this.brandName.set(brand.name);
      });
  }

  getProducts(
    pagination: IPagination,
    filters?: IFilter[],
    sorting?: ISorting
  ): Observable<IGetPaginatedResponse<IProduct[]>> {
    return this.#categoriesService.getBrandProducts(this.brandId(), pagination, filters, sorting);
  }
}
